import { Controller } from "@hotwired/stimulus";

export default class EthereumController extends Controller {
  static values = {
    fromAccount: String,
    toAccount: String,
    amount: String,
    nftNumber: String,
    marketplaceContract: String,
    nftContract: String,
    tokenId: String,
    chainListingId: String,
    priceWei: String
  };

  async transferEth() {
    if (globalThis.ethereum === undefined) {
      return globalThis.Swal.fire({
        customClass: {
          popup: "eth-install-popup swal-popup alert-sm-bg align-items-center justify-content-center w-100",
          htmlContainer: "text-black-paint text-white"
        },
        animation: false,
        title: "Install MetaMask",
        html: "<a class='text-white-paint text-primary fs-30' href='https://metamask.io/download' target='_blank' rel='noopener noreferrer'>Install Now</a>",
        icon: "error",
        confirmButtonText: "OK"
      });
    }

    if (!this.marketplaceContractValue || !this.nftContractValue || !this.tokenIdValue || !this.chainListingIdValue) {
      return this.showMessage("This NFT is not ready for an on-chain marketplace purchase.", "error");
    }

    try {
      await this.ensureSepolia();
      const accounts = await globalThis.ethereum.request({ method: "eth_requestAccounts" });
      const buyer = accounts[0]?.toLowerCase();
      if (buyer !== this.fromAccountValue.toLowerCase()) {
        throw new Error("The active MetaMask account does not match your verified wallet. Select Change wallet and verify this account before purchasing.");
      }

      globalThis.Swal.fire({
        customClass: { popup: "swal-popup alert-sm-bg align-items-center justify-content-center w-100" },
        title: "Confirm purchase",
        html: "Confirm the marketplace transaction in MetaMask, then wait for Sepolia.",
        allowOutsideClick: false,
        didOpen: () => globalThis.Swal.showLoading()
      });

      const txHash = await globalThis.ethereum.request({
        method: "eth_sendTransaction",
        params: [{
          from: buyer,
          to: this.marketplaceContractValue,
          value: `0x${BigInt(this.priceWeiValue).toString(16)}`,
          data: `0xefef39a1${this.uintWord(this.chainListingIdValue)}`
        }]
      });
      await this.waitForReceipt(txHash);
      await this.confirmPurchase(txHash);
      await globalThis.Swal.fire({
        customClass: { popup: "swal-popup alert-sm-bg align-items-center justify-content-center w-100" },
        html: "NFT purchased and ownership verified on Sepolia.",
        icon: "success",
        timer: 2200,
        showConfirmButton: false
      });
      globalThis.location.href = "/nfts?tab=purchased";
    } catch (error) {
      return this.showMessage(error.message || "NFT purchase failed.", "error");
    }
  }

  async ensureSepolia() {
    const chainId = await globalThis.ethereum.request({ method: "eth_chainId" });
    if (chainId.toLowerCase() !== "0xaa36a7") {
      await globalThis.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0xaa36a7" }] });
    }
  }

  async waitForReceipt(txHash) {
    const deadline = Date.now() + 180000;
    while (Date.now() < deadline) {
      const receipt = await globalThis.ethereum.request({ method: "eth_getTransactionReceipt", params: [txHash] });
      if (receipt) {
        if (BigInt(receipt.status || "0x0") !== 1n) throw new Error("The marketplace transaction reverted.");
        return receipt;
      }
      await new Promise((resolve) => setTimeout(resolve, 2500));
    }
    throw new Error("The purchase is still pending. Check Sepolia Etherscan before trying again.");
  }

  async confirmPurchase(txHash) {
    const response = await fetch(`/nfts/${this.nftNumberValue}/purchase`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ tx_hash: txHash })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.errors?.join(", ") || "Rails could not verify the purchase.");
    return payload;
  }

  uintWord(value) {
    return BigInt(value).toString(16).padStart(64, "0");
  }

  showMessage(message, icon) {
    return globalThis.Swal.fire({
      customClass: {
        popup: "swal-popup alert-sm-bg align-items-center justify-content-center w-100",
        htmlContainer: "text-black-paint text-white"
      },
      animation: false,
      html: message,
      icon,
      confirmButtonText: "OK"
    });
  }
}
